import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AutoComplete, Input, Spin, Typography } from 'antd'
import {
  DaDataPartySuggestion,
  findPartyByInn,
  mapPartyToRequisites,
  PollRequisitePatch,
  suggestParty,
} from '../dadata-party'

const { Text } = Typography

type Props = {
  value?: string
  onChange?: (value: string) => void
  onPartyFill?: (patch: PollRequisitePatch) => void
}

type PartyOption = { value: string; label: React.ReactNode; party: DaDataPartySuggestion }

const SEARCH_DEBOUNCE_MS = 300

function digitsOnly(value: string): string {
  return String(value || '').replace(/\D/g, '')
}

function isFullInn(value: string): boolean {
  return /^(\d{10}|\d{12})$/.test(value)
}

function isAbortError(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === 'AbortError'
}

function toOption(party: DaDataPartySuggestion): PartyOption {
  return {
    value: party.data?.inn || party.value,
    party,
    label: (
      <div>
        <div>{party.value}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {[party.data?.inn, party.data?.kpp, party.data?.address?.value]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </div>
    ),
  }
}

/**
 * ИНН с подсказками DaData.
 * Запросы — только при вводе (debounce) и при выборе подсказки; на blur ничего не ищем и фокус
 * никогда не возвращаем. Каждый новый запрос отменяет предыдущий (AbortController), устаревшие
 * ответы игнорируются, поэтому вручную введённые название / КПП / адрес и т.д. не затираются.
 */
export const PollInnField: React.FC<Props> = ({ value = '', onChange, onPartyFill }) => {
  const [options, setOptions] = useState<PartyOption[]>([])
  const [loading, setLoading] = useState(false)
  const [hint, setHint] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const requestIdRef = useRef(0)
  /** ИНН, по которому уже искали точное совпадение (найден или нет) — повторно не ищем */
  const lookedUpInnRef = useRef<string>('')
  const valueRef = useRef(value)
  const onPartyFillRef = useRef(onPartyFill)
  const onChangeRef = useRef(onChange)

  useEffect(() => {
    valueRef.current = value
    onPartyFillRef.current = onPartyFill
    onChangeRef.current = onChange
  }, [value, onPartyFill, onChange])

  /** Отменяет ожидающий debounce и запрос «в полёте»; спиннер гасится сразу. */
  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    abortRef.current?.abort()
    abortRef.current = null
    requestIdRef.current += 1
    setLoading(false)
  }, [])

  useEffect(() => cancelPending, [cancelPending])

  const applyParty = useCallback((party: DaDataPartySuggestion) => {
    const patch = mapPartyToRequisites(party)
    const inn = patch['requisite.inn'] || digitsOnly(String(valueRef.current || ''))
    if (inn) {
      onChangeRef.current?.(inn)
      lookedUpInnRef.current = inn
    }
    onPartyFillRef.current?.(patch)
    setHint(
      'Данные организации подставляются автоматически, проверьте и при необходимости поправьте',
    )
    setUnavailable(false)
  }, [])

  const search = useCallback(
    (query: string) => {
      cancelPending()
      const q = String(query || '').trim()
      if (q.length < 3) {
        setOptions([])
        return
      }
      const isInn = isFullInn(q)
      if (isInn && q === lookedUpInnRef.current) return

      timerRef.current = setTimeout(async () => {
        timerRef.current = null
        const controller = new AbortController()
        abortRef.current = controller
        const requestId = ++requestIdRef.current
        const isCurrent = () => requestId === requestIdRef.current && !controller.signal.aborted
        setLoading(true)
        try {
          if (isInn) {
            const party = await findPartyByInn(q, controller.signal)
            if (!isCurrent()) return
            lookedUpInnRef.current = q
            if (party) {
              setOptions([toOption(party)])
              applyParty(party)
            } else {
              setOptions([])
              setHint('Организация не найдена — заполните поля вручную')
              setUnavailable(false)
            }
          } else {
            const suggestions = await suggestParty(q, 8, controller.signal)
            if (!isCurrent()) return
            setOptions(suggestions.map(toOption))
            setUnavailable(false)
          }
        } catch (error) {
          if (isAbortError(error) || !isCurrent()) return
          setOptions([])
          setUnavailable(true)
          setHint('DaData недоступен — заполните реквизиты вручную')
        } finally {
          if (requestId === requestIdRef.current) {
            abortRef.current = null
            setLoading(false)
          }
        }
      }, SEARCH_DEBOUNCE_MS)
    },
    [applyParty, cancelPending],
  )

  const inputValue = useMemo(() => (typeof value === 'string' ? value : ''), [value])

  return (
    <div style={{ maxWidth: 400 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <AutoComplete
          value={inputValue}
          options={options}
          style={{ width: '100%' }}
          onChange={text => {
            const next = String(text || '')
            onChangeRef.current?.(next)
            const digits = digitsOnly(next)
            // ИНН отредактирован после поиска — сбрасываем подсказку и разрешаем новый поиск.
            // (При выборе подсказки ИНН совпадает с найденным — повторного запроса не будет.)
            if (digits !== lookedUpInnRef.current) {
              lookedUpInnRef.current = ''
              setHint(null)
              setUnavailable(false)
            }
            // Полный ИНН — точный поиск с автозаполнением; иначе — подсказки
            search(isFullInn(next.trim()) ? digits : next)
          }}
          onSelect={(_val, option: any) => {
            if (option?.party) {
              cancelPending()
              applyParty(option.party as DaDataPartySuggestion)
            }
          }}
          notFoundContent={loading ? <Spin size="small" /> : null}
        >
          <Input
            placeholder="ИНН (10 или 12 цифр) — заполним данные автоматически"
            maxLength={12}
            // Ушли из поля — пользователь заполняет вручную: отменяем поиск, ответы больше не применяем.
            onBlur={cancelPending}
          />
        </AutoComplete>
        {loading ? <Spin size="small" /> : null}
      </div>
      {hint ? (
        <Text
          type={unavailable ? 'warning' : 'secondary'}
          style={{ display: 'block', marginTop: 6, fontSize: 12 }}
        >
          {hint}
        </Text>
      ) : null}
    </div>
  )
}

export default PollInnField
